import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useState } from 'react';
import { Icon } from '../../../../../../base/components/Icon';
import { useAppDispatch, useAppSelector } from '../../../../../../base/hooks';
import { DatePeriod } from '../../../enums';
import { responsesActions } from '../../../redux/slices/responsesSlice';
import { mapDatePeriodToText } from '../../../utils';
import moment from 'moment';
import { DatePickerModal } from '../DatePickerModal';

import './styles.scss';

interface Props {
    className?: string;
}

export const DateRangeSelect = ({ className }: Props) => {
    const dispatch = useAppDispatch();
    const { datePeriod, dateFrom, dateTo } = useAppSelector((state) => state.employerResponses.filters);
    const [showDatePickerModal, setShowDatePickerModal] = useState(false);

    const handleChange = (event: SelectChangeEvent<DatePeriod>) => {
        const selectedDatePeriod = event.target.value as DatePeriod;
        dispatch(responsesActions.setFiltersDatePeriod(selectedDatePeriod));
        if (selectedDatePeriod === DatePeriod.CustomPeriod) {
            return;
        }

        let dateFromNewValue: Date | undefined = undefined;
        let dateToNewValue: Date | undefined = undefined;

        switch (selectedDatePeriod) {
            case DatePeriod.Today:
                dateFromNewValue = moment().startOf('day').toDate();
                break;
            case DatePeriod.Yesterday:
                dateFromNewValue = moment().subtract(1, 'days').startOf('day').toDate();
                dateToNewValue = moment().subtract(1, 'days').endOf('day').toDate();
                break;
            case DatePeriod.Last7Days:
                dateFromNewValue = moment().subtract(7, 'days').startOf('day').toDate();
                break;
            case DatePeriod.Last30Days:
                dateFromNewValue = moment().subtract(30, 'days').startOf('day').toDate();
                break;
            case DatePeriod.AllTime:
            default:
                break;
        }
        dispatch(responsesActions.setFiltersDates({ dateFrom: dateFromNewValue, dateTo: dateToNewValue }));
    };

    const clearFilter = () => {
        dispatch(responsesActions.setFiltersDatePeriod(DatePeriod.AllTime));
        dispatch(responsesActions.setFiltersDates({ dateFrom: undefined, dateTo: undefined }));
    };

    const hideModal = () => setShowDatePickerModal(false);

    const closeHandler = () => {
        setTimeout(() => {
            const element = document.activeElement as HTMLElement;
            element?.blur();
        }, 0);
    };

    return (
        <div className='responses-filters-select-relative-wrap'>
            <Select
                className={`responses-filters-select ${className}`}
                value={datePeriod}
                onChange={handleChange}
                onClose={closeHandler}
                variant='standard'
                disableUnderline
                MenuProps={{
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                    },
                    classes: { paper: 'responses-filters-select__dropdown', list: 'responses-filters-select__list' },
                }}
                IconComponent={(props) => <Icon icon='chevron-down-regular' size='14px' {...props} />}
                renderValue={(value: DatePeriod) => {
                    if (value === DatePeriod.AllTime) {
                        return mapDatePeriodToText(value);
                    }

                    if (value !== DatePeriod.CustomPeriod) {
                        return (
                            <div className='responses-filters-select__render-value' title={mapDatePeriodToText(value)}>
                                {mapDatePeriodToText(value)}
                            </div>
                        );
                    }

                    const renderValue =
                        dateFrom && dateTo
                            ? `${moment(dateFrom).format('DD MMM YYYY')}-${moment(dateTo).format('DD MMM YYYY')}`
                            : '';
                    return (
                        <div className='responses-filters-select__render-value' title={renderValue}>
                            {renderValue}
                        </div>
                    );
                }}
            >
                <MenuItem className='responses-filters-select__item responses-filters-select__item--hidden' value={DatePeriod.AllTime}>
                    {mapDatePeriodToText(DatePeriod.AllTime)}
                </MenuItem>
                <MenuItem className='responses-filters-select__item' value={DatePeriod.Today}>
                    {mapDatePeriodToText(DatePeriod.Today)}
                </MenuItem>
                <MenuItem className='responses-filters-select__item' value={DatePeriod.Yesterday}>
                    {mapDatePeriodToText(DatePeriod.Yesterday)}
                </MenuItem>
                <MenuItem className='responses-filters-select__item' value={DatePeriod.Last7Days}>
                    {mapDatePeriodToText(DatePeriod.Last7Days)}
                </MenuItem>
                <MenuItem className='responses-filters-select__item' value={DatePeriod.Last30Days}>
                    {mapDatePeriodToText(DatePeriod.Last30Days)}
                </MenuItem>
                <MenuItem
                    className='responses-filters-select__item'
                    value={DatePeriod.CustomPeriod}
                    onClick={() => setShowDatePickerModal(true)}
                >
                    {mapDatePeriodToText(DatePeriod.CustomPeriod)}
                </MenuItem>
            </Select>
            {datePeriod !== DatePeriod.AllTime && (
                <div onClick={clearFilter} className='responses-filters-select__clear-filter-button'>
                    <Icon icon='xmark-regular' size='14px' />
                </div>
            )}
            {datePeriod === DatePeriod.CustomPeriod && (
                <DatePickerModal
                    visible={showDatePickerModal}
                    closeHandler={hideModal}
                    onModalCloseCallback={clearFilter}
                />
            )}
        </div>
    );
};
